import { useState, useEffect, useCallback } from "react";
import { apiClient } from "@/lib/api-client.js";

type SettingsValues = Record<string, unknown>;
interface GetResponse { category: string; values: SettingsValues }
interface PutResponse { category: string; key: string; values: SettingsValues }

export interface UseSettings {
  values: SettingsValues | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  saved: boolean;
  /** Sendet je geändertem Schlüssel ein PUT /settings/:category/:key. */
  save: (patch: SettingsValues) => Promise<boolean>;
  reload: () => void;
}

export function useSettings(category: string): UseSettings {
  const [values, setValues] = useState<SettingsValues | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    void apiClient
      .get<GetResponse>(`/admin/settings/${category}`)
      .then((res) => {
        if (res.success) setValues(res.data.values);
        else setError(res.error.message);
      })
      .catch(() => setError("Einstellungen konnten nicht geladen werden."))
      .finally(() => setLoading(false));
  }, [category]);

  useEffect(() => load(), [load]);

  const save = useCallback(
    async (patch: SettingsValues): Promise<boolean> => {
      const keys = Object.keys(patch);
      if (keys.length === 0) return true;
      setSaving(true);
      setError(null);
      setSaved(false);
      try {
        // Pro Schlüssel ein eigener, einzeln validierter + auditierter Write.
        for (const key of keys) {
          const res = await apiClient.put<PutResponse>(`/admin/settings/${category}/${key}`, { value: patch[key] });
          if (!res.success) {
            setError(res.error.message);
            return false;
          }
          setValues(res.data.values);
        }
        setSaved(true);
        window.setTimeout(() => setSaved(false), 2500);
        return true;
      } catch {
        setError("Speichern fehlgeschlagen.");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [category],
  );

  return { values, loading, saving, error, saved, save, reload: load };
}
